import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import { DsSection, VALID_DS_SECTIONS } from 'designSystem/types';
import { ExportFramesAiPreset } from 'dragonmouse-shared/api/export';
import { SelectionInfo } from 'dragonmouse-shared/devigner';
import {
	distanceBetweenPoints,
	Point,
	Rect,
	rectContainsRect,
	rectsOverlap,
} from 'dragonmouse-shared/geometry';
import {
	ChatEntry,
	ChatEntryRestriction,
	Device,
	Frame,
	FRAME_GAP,
	FrameStatus,
	FrameType,
	Scene,
	SceneChat,
	SceneComment,
} from 'dragonmouse-shared/interfaces';
import { transformCanvasToShowSceneBoundingBox } from 'redux/mode/actions';
import { getVisibleCanvasCenterInSceneCoordinates } from 'redux/mode/util';
import store from 'redux/store';
import { frameImageUrl, frameSiteExportUrl, frameSiteUrl } from 'utils';
import { v4 as uuid } from 'uuid';
import { isForgeOrigin, sendToDevigner } from '../../devigner';
import { selectFrames, selectIsGuest, selectSceneComments } from './reducers';

// EVERYTHING IN THIS FILE IS IN SCENE COORDINATES
// (unless otherwise noted)

//-----------------------------------------------------------------------------
// scene
//-----------------------------------------------------------------------------

export const canEdit = (scene: Scene | null): boolean => {
	if (!scene) return false;
	const state = store.getState();
	return state.db.scenes.some((s) => s.id === scene.id);
};

export const canEditFrame = (frame: Frame | null): boolean => {
	if (!frame) return false;
	const state = store.getState();
	return state.db.scenes.some((s) => s.id === frame.sceneId);
};

export type DsEditBlockReason = 'guest' | 'historical' | 'read-only';

// returns the block reason if editing is blocked, or null if editing is allowed
export const designSystemEditingBlocked = (): DsEditBlockReason | null => {
	const state = store.getState();
	if (selectIsGuest(state.db)) return 'guest';
	if (state.mode.historicalDsVersionNumber !== null) return 'historical';
	const dsId = state.mode.activeDesignSystemId;
	if (dsId && !state.db.designSystems.some((ds) => ds.id === dsId)) return 'read-only';
	return null;
};

const LEGACY_XML_TAG_RE = /<(theme|styles|components|gallery|guidelines|config|assets)>/;

// returns true if the chat has legacy XML-era entries that would poison the LLM context
export const isDsLegacyChat = (entries: ChatEntry[]): boolean => {
	const match = entries.find(
		(entry) => entry.kind === 'system' && LEGACY_XML_TAG_RE.test(entry.message)
	);
	return !!match;
};

const LEGACY_SCENE_XML_TAG_RE = /<(design_group|design(?:\s|>))/;

export const isSceneLegacyChat = (entries: ChatEntry[]): boolean => {
	return entries.some(
		(entry) => entry.kind === 'system' && LEGACY_SCENE_XML_TAG_RE.test(entry.message)
	);
};

//-----------------------------------------------------------------------------
// frame
//-----------------------------------------------------------------------------

// if a frame contains the provided rect, it is returned
// if no frames contain the rect, undefined is returned
//
// rect is in scene coordinates (this should be obvious as this is a scene util method)
//
// if multiple frames contain the provided rect, the one that's last in the frame list is returned
// (currently, frames are rendered in array order, so the topmost frame will be returned)
export const getFrameContaining = (rect: Rect): Frame | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);

	// Find the last frame that contains the rect (visually on top)
	for (let i = frames.length - 1; i >= 0; i--) {
		const frame = frames[i];
		const bounds = getFrameDisplayBounds(frame);
		if (bounds && rectContainsRect(bounds, rect)) {
			return frame;
		}
	}

	return null;
};

// Hit-tests scene comments. Returns the comment whose marker contains the point,
// using a fixed screen-radius converted to scene coords via scale.
// Hit-test against the 28x28 marker rect (centered horizontally, extending upward from anchor)
export const getCommentContaining = (scene: Point, scale: number): SceneComment | null => {
	const state = store.getState();
	const comments = selectSceneComments(state.db);
	const halfW = 14 / scale;
	const h = 28 / scale;
	for (let i = comments.length - 1; i >= 0; i--) {
		const c = comments[i];
		if (scene.x >= c.x - halfW && scene.x <= c.x + halfW && scene.y >= c.y - h && scene.y <= c.y) {
			return c;
		}
	}
	return null;
};

export const getFrame = (id: string): Frame | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	return frames.find((frame) => frame?.id === id) || null;
};

export const getFrames = (ids: string[]): Frame[] => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	return frames.filter((frame) => ids.includes(frame.id));
};

export enum Direction {
	RIGHT = 'right',
	LEFT = 'left',
	UP = 'up',
	DOWN = 'down',
}

export const getAdjacentFrame = (frameId: string, direction: Direction): Frame | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	const frame = frames.find((f) => f.id === frameId) || null;
	if (!frame) return null;

	const b = getFrameDisplayBounds(frame);
	const directionFilters = {
		[Direction.RIGHT]: (f: Frame) => {
			const fb = getFrameDisplayBounds(f);
			return (
				f.id !== frame.id && fb.x > b.x + b.width && fb.y + fb.height > b.y && fb.y < b.y + b.height
			);
		},
		[Direction.LEFT]: (f: Frame) => {
			const fb = getFrameDisplayBounds(f);
			return (
				f.id !== frame.id &&
				fb.x + fb.width < b.x &&
				fb.y + fb.height > b.y &&
				fb.y < b.y + b.height
			);
		},
		[Direction.DOWN]: (f: Frame) => {
			const fb = getFrameDisplayBounds(f);
			return (
				f.id !== frame.id && fb.y > b.y + b.height && fb.x + fb.width > b.x && fb.x < b.x + b.width
			);
		},
		[Direction.UP]: (f: Frame) => {
			const fb = getFrameDisplayBounds(f);
			return (
				f.id !== frame.id && fb.y + fb.height < b.y && fb.x + fb.width > b.x && fb.x < b.x + b.width
			);
		},
	};

	const adjacentFrames = frames.filter(directionFilters[direction]);

	const comparators = {
		[Direction.RIGHT]: (f: Frame) => f.x,
		[Direction.LEFT]: (f: Frame) => -f.x,
		[Direction.DOWN]: (f: Frame) => f.y,
		[Direction.UP]: (f: Frame) => -f.y,
	};

	// return the closest frame in the specified direction
	return adjacentFrames.length > 0
		? adjacentFrames.reduce((closest, current) =>
				comparators[direction](current) < comparators[direction](closest) ? current : closest
		  )
		: null;
};

export const getFrameToTheRight = (frameId: string): Frame | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	const frame = frames.find((f) => f.id === frameId) || null;
	if (!frame) return null;

	// find all the frames to the right of the current frame that also have some
	// overlap along the y axis
	const b = getFrameDisplayBounds(frame);
	const framesToTheRight = frames.filter((f) => {
		const fb = getFrameDisplayBounds(f);
		return (
			f.id !== frame.id && fb.x > b.x + b.width && fb.y + fb.height > b.y && fb.y < b.y + b.height
		);
	});

	// return the frame to the right with the lowest x value
	return framesToTheRight.reduce((leastXFrame, currentFrame) => {
		return currentFrame.x < leastXFrame.x ? currentFrame : leastXFrame;
	}, framesToTheRight[0]);
};

export const getFrameNearestScenePoint = (point: Point): Frame | undefined => {
	const state = store.getState();
	const frames = selectFrames(state.db);

	let closestFrame: Frame | undefined;
	let closestDistance = Infinity;

	frames.forEach((frame: Frame) => {
		const center = getFrameCenter(frame);
		const distance = distanceBetweenPoints(center, point);
		if (distance < closestDistance) {
			closestFrame = frame;
			closestDistance = distance;
		}
	});

	return closestFrame;
};

export const getFrameDisplayBounds = (frame: Frame): Rect => {
	const s = frame.metadata?.displayScale ?? 1;
	return {
		x: frame.x,
		y: frame.y,
		width: frame.width * s,
		height: frame.height * s,
	};
};

export const getFrameCenter = (frame: Frame): Point => {
	const bounds = getFrameDisplayBounds(frame);
	return {
		x: bounds.x + bounds.width / 2,
		y: bounds.y + bounds.height / 2,
	};
};

export enum SearchDirection {
	RIGHT = 'right',
	DOWN = 'down',
}

// Given "bounds" which is a rect in scene coordinates,
//  - is bounds unoccupied? If so, return bounds.
//  - if not, search in the provided direction until we find an unoccupied spot, and return that.
//
// Currently, this routine fans out a bit as it searches. So if direction is down,
// it not only shifts down but also to the left and right a bit to find a good spot.
export const findNextFramePosition = (
	bounds: Rect,
	direction: SearchDirection = SearchDirection.DOWN
): Rect => {
	const state = store.getState();
	const frames = selectFrames(state.db);

	// gather all items in the scene into a collection of bounds we will test against
	const testBoundsCollection: Rect[] = [...frames.map((frame) => getFrameDisplayBounds(frame))];

	let current = { x: bounds.x, y: bounds.y };
	let hasOverlap = true;
	let iterations = 0;
	const MAX_ITERATIONS = 100;

	while (hasOverlap && iterations < MAX_ITERATIONS) {
		iterations++;
		hasOverlap = false;

		// Test against all bounds (frames and chats)
		for (const testBounds of testBoundsCollection) {
			if (
				rectsOverlap(
					{ x: current.x, y: current.y, width: bounds.width, height: bounds.height },
					testBounds
				)
			) {
				hasOverlap = true;
				switch (direction) {
					case SearchDirection.RIGHT:
						current.x = testBounds.x + testBounds.width + FRAME_GAP;
						break;
					case SearchDirection.DOWN:
						current.y = testBounds.y + testBounds.height + FRAME_GAP;
						break;
				}
				break;
			}
		}
	}

	return { x: current.x, y: current.y, width: bounds.width, height: bounds.height };
};

export const getRightmostFrameX = (): number | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	if (frames.length === 0) return null;
	const rightmostFrameX = frames.reduce((max, frame) => {
		const b = getFrameDisplayBounds(frame);
		return Math.max(max, b.x + b.width);
	}, 0);
	return rightmostFrameX;
};

export const getFrameImage = async (frame: Frame): Promise<Blob> => {
	const url = frameImageUrl(frame, true);
	if (!url) throw new Error('No URL for screen.');
	const response = await fetch(url, {
		credentials: 'include', // This ensures cookies/auth are sent
	});
	if (!response.ok) throw new Error('Failed to fetch image');

	const reader = response.body?.getReader();
	if (!reader) throw new Error('No readable stream available');

	const chunks: Uint8Array[] = [];
	while (true) {
		const { done, value } = await reader.read();
		if (done) break;
		chunks.push(value);
	}

	const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
	const concatenated = new Uint8Array(totalLength);
	let position = 0;
	for (const chunk of chunks) {
		concatenated.set(chunk, position);
		position += chunk.length;
	}

	return new Blob([concatenated], { type: 'image/png' });
};

export const getFrameSvg = async (frame: Frame): Promise<Blob> => {
	if (!frame.siteId) {
		throw new Error('Frame has no siteId');
	}

	const siteUrl = frameSiteUrl(frame, { devignSvg: true });
	if (!siteUrl) {
		throw new Error('Could not generate site URL');
	}

	const svg = await new Promise<string>((resolve, reject) => {
		// Create an invisible iframe
		const iframe = document.createElement('iframe');
		iframe.style.visibility = 'hidden';
		iframe.style.pointerEvents = 'none';
		iframe.src = siteUrl;
		iframe.style.width = frame.width.toString() + 'px';
		iframe.style.height = frame.height.toString() + 'px';

		// Set up message listener
		const messageHandler = (event: MessageEvent) => {
			if (!isForgeOrigin(event)) return;
			if (event.data?.type !== 'pageSvg') return;
			if (event.data.siteId !== frame.siteId) return;

			// Clear the timeout since we got our data
			clearTimeout(timeoutId);

			// Remove iframe after we get our image
			window.removeEventListener('message', messageHandler);
			document.body.removeChild(iframe);

			// Process the SVG
			const svg = event.data.svg as string;
			if (!svg) {
				reject(new Error('No SVG data received'));
				return;
			}
			resolve(svg);
		};

		window.addEventListener('message', messageHandler);
		document.body.appendChild(iframe);

		// Set a timeout in case the iframe never responds
		const timeoutId = setTimeout(() => {
			window.removeEventListener('message', messageHandler);
			document.body.removeChild(iframe);
			reject(new Error('Timeout waiting for SVG data'));
		}, 15000); // 15 second timeout
	});

	const response = await forge.inlineSvg({
		data: svg,
		width: frame.width,
		height: frame.height,
	});
	if (!response.data) throw new Error('Failed to fetch convert SVG to inline');

	return new Blob([response.data], { type: 'image/svg+xml' });
};

// Packages one-or-more frames into a downloadable SVG asset entirely on the
// client — each frame goes through the proven getFrameSvg iframe+postMessage
// path, and multi-frame exports are zipped with JSZip. The returned blob is
// intended to be base64-encoded and uploaded via forge.exportPrebuiltAsset.
export const packageFramesSvgAsset = async (
	frames: Frame[]
): Promise<{ blob: Blob; extension: 'svg' | 'zip' }> => {
	if (frames.length === 0) throw new Error('No frames to export');
	if (frames.length === 1) {
		const blob = await getFrameSvg(frames[0]);
		return { blob, extension: 'svg' };
	}
	const { default: JSZip } = await import('jszip');
	const zip = new JSZip();
	const used = new Map<string, number>();
	for (const frame of frames) {
		const stripped = (frame.title || 'untitled').replace(/[/\\?%*:|"<>]/g, '_');
		// Neutralize dot-only names so zip entries can't be "." or ".." — defends
		// downstream extractors against zip-slip even though JSZip itself is fine.
		const safe = /^\.+$/.test(stripped) ? '_' : stripped;
		const count = used.get(safe) ?? 0;
		used.set(safe, count + 1);
		const folderName = count === 0 ? safe : `${safe} (${count})`;
		const folder = zip.folder(folderName);
		if (!folder) continue;
		const svgBlob = await getFrameSvg(frame);
		folder.file('frame.svg', await svgBlob.text());
	}
	const blob = await zip.generateAsync({ type: 'blob' });
	return { blob, extension: 'zip' };
};

// Reads a Blob as a base64 string (without the data-URL prefix) for JSON
// upload. Used by the client-packaged SVG export path.
export const blobToBase64 = async (blob: Blob): Promise<string> => {
	const buf = await blob.arrayBuffer();
	const bytes = new Uint8Array(buf);
	let binary = '';
	for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
	return btoa(binary);
};

export const getFrameExportHtml = async (frame: Frame): Promise<Blob> => {
	if (!frame.siteId) throw new Error('Frame has no siteId');

	const url = frameSiteExportUrl(frame);
	if (!url) throw new Error('Could not generate site URL');

	const response = await fetch(url, {
		credentials: 'include', // This ensures cookies/auth are sent
	});
	if (!response.ok) throw new Error('Failed to fetch HTML');

	const html = await response.text();
	return new Blob([html], { type: 'text/html' });
};

// frames don't have a device, they just have a width and height! so we have this procedure to try to generate the right one
// looks through the provided devices array and returns the first Device that matches the frame's width and height
export const getDeviceFromFrameId = (frameId: string, devices: Device[]): Device | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	const frame = frames.find((frame) => frame?.id === frameId) || null;

	if (devices.length === 0) {
		return null;
	}
	for (const d of devices) {
		if (d.width === frame?.width && d.height === frame?.height) {
			return d;
		}
	}
	return null;
};

export const getFrameIconType = (frame: Frame): IconType => {
	switch (frame.metadata.type) {
		case FrameType.ADJUST:
			return IconType.ADJUST;
		case FrameType.GENERATE:
		case FrameType.GENERATE_FROM_CHAT:
			return IconType.GENERATE;
		case FrameType.IMPORT_SITE:
		case FrameType.IMPORT:
			return IconType.IMPORT;
		case FrameType.MERGE:
			return IconType.MERGE;
		case FrameType.PROTOTYPE:
			return IconType.PROTOTYPE;
	}
	return IconType.FRAME;
};

export const canRegenerateFrame = (frame: Frame): boolean => {
	if (frame.metadata.status !== FrameStatus.READY) return false;
	return (
		frame.metadata.type === FrameType.GENERATE ||
		frame.metadata.type === FrameType.GENERATE_FROM_CHAT
	);
};

export const canEnhanceFrame = (frame: Frame): boolean => {
	if (frame.metadata.status !== FrameStatus.READY) return false;
	return frame.metadata.type !== FrameType.IMPORT && frame.metadata.type !== FrameType.SYSTEM;
};

export const canExportSvg = (frames: Frame[]): boolean => {
	if (frames.every((frame) => frame.metadata.type === FrameType.IMPORT)) return false;
	return true;
};

// creates a group of count frames of the provided type, device, etc.
//
// if relativeToBounds is provided, try to place the new frames relative to these bounds
// if not, try to place the frames in the center of the screen
// (if these spots are taken, search for a sensible available spot to put them)
export const createFrames = async (
	count: number,
	device: Device,
	frameType: FrameType,
	titlePrefix: string,
	displayLabels: Record<string, string>,
	sourceFrameIds: string[],
	sourceChatId: string | null,
	relativeToBounds: Rect | null
): Promise<string[]> => {
	const state = store.getState();
	const activeSceneId = state.mode.activeSceneId;

	if (!activeSceneId) {
		throw new Error('No active scene ID found');
	}

	const totalWidth = count * device.width + (count - 1) * FRAME_GAP;
	const totalHeight = device.height;

	const sourceFrames = getFrames(sourceFrameIds);
	const hasChatSource: boolean = sourceFrames.some((frame) => frame.metadata.sourceChatId);
	const direction: SearchDirection = hasChatSource ? SearchDirection.RIGHT : SearchDirection.DOWN;

	const idealBounds: Rect = {
		x: 0,
		y: 0,
		width: totalWidth,
		height: totalHeight,
	};

	if (relativeToBounds) {
		// set x and y so that idealBounds is "direction" away from the relativeToBounds
		if (direction === SearchDirection.RIGHT) {
			idealBounds.x = relativeToBounds.x + relativeToBounds.width + FRAME_GAP;
			// the y-center of the idealBounds should be the same as the y-center of the relativeToBounds
			// relativeToBounds.y + relativeToBounds.height / 2 == idealBounds.y + totalHeight / 2
			idealBounds.y = Math.round(
				relativeToBounds.y + relativeToBounds.height / 2 - totalHeight / 2
			);
		} else {
			// the x-center of the idealBounds should be the same as the x-center of the relativeToBounds
			// relativeToBounds.x + relativeToBounds.width / 2 == idealBounds.x + totalWidth / 2
			idealBounds.x = Math.round(relativeToBounds.x + relativeToBounds.width / 2 - totalWidth / 2);
			// the top of the idealBounds should be the same as the top of the relativeToBounds
			idealBounds.y = relativeToBounds.y + relativeToBounds.height + FRAME_GAP;
		}
	} else {
		// set x and y so that the center of the idealBounds is in the center of the screen
		const center = getVisibleCanvasCenterInSceneCoordinates();
		idealBounds.x = Math.round(center.x - totalWidth / 2);
		idealBounds.y = Math.round(center.y - totalHeight / 2);
	}

	// Find an empty spot to place the entire group of frames
	const newPosition = findNextFramePosition(idealBounds, direction);

	const targetFrameIds: string[] = [];
	const frameCreationPromises: Promise<void>[] = [];
	for (let i = 0; i < count; i++) {
		const frameId = uuid();
		targetFrameIds.push(frameId);
		frameCreationPromises.push(
			forge.createFrame({
				id: frameId,
				sceneId: activeSceneId,
				title: `${titlePrefix}${i + 1}`,
				x: newPosition.x + i * (device.width + FRAME_GAP),
				y: newPosition.y,
				width: device.width,
				height: device.height,
				metadata: {
					type: frameType,
					status: FrameStatus.PROCESSING,
					statusMessage: 'initializing design generation',
					displayLabels,
					sourceFrameIds,
					sourceChatId: sourceChatId || undefined,
				},
			})
		);
	}

	// Wait for database to create all frames
	await Promise.all(frameCreationPromises);

	const boundingBox: Rect = {
		x: newPosition.x,
		y: newPosition.y,
		width: totalWidth,
		height: device.height,
	};
	store.dispatch(transformCanvasToShowSceneBoundingBox(boundingBox));

	return targetFrameIds;
};

//-----------------------------------------------------------------------------
// chat
//-----------------------------------------------------------------------------

// does this chat have any generated frames associated with it?
// if so, return true.
// note this will return true even if these frames are not currently placed on the canvas
export const chatHasFrames = (chat: SceneChat): boolean => {
	for (const entry of chat.entries) {
		if (
			entry.kind === 'system' &&
			(!entry.restriction || entry.restriction === ChatEntryRestriction.USER)
		) {
			const parts = splitMessageWithFrames(entry.message);
			if (parts.some((part) => part.type === 'frames')) {
				return true;
			}
		}
	}
	return false;
};

// given a <frames> XML block from a chat entry, return an array of frame ids
export const parseFramesXml = (xml: string): string[] => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(xml, 'text/xml');
	const frameElements = doc.querySelectorAll('*|frame');
	return Array.from(frameElements).map((frame) => frame.getAttribute('id') || '');
};

export type ChatMessagePart =
	| ChatMessageFramesPart
	| ChatMessageTextPart
	| ChatMessageDsUpdatePart
	| ChatMessageActionPart
;
export type ChatMessageFramesPart = {
	type: 'frames';
	frameIds: string[];
};
export type ChatMessageTextPart = {
	type: 'text';
	content: string;
};
export type ChatMessageDsUpdatePart = {
	type: 'ds-update';
	section: DsSection;
	paths: string[];
};
export type ChatAction =
	| { kind: 'try-in-project'; label: string; prompt?: string; sceneId?: string }
	// DEPRECATED: superseded by export-ds-ai. Kept so old chat messages still render.
	| { kind: 'download-ds'; format: 'bundle' | 'tokens-json'; label?: string }
	| { kind: 'show'; path?: string }
	| { kind: 'generate-gallery'; label: string; prompt: string }
	// several of the actions below have static labels. we could clean up by removing label parameter and hardcoding them in studio. for now i'm cool with keeping it in - ss
	| { kind: 'connect-figma'; label: string }
	| { kind: 'reconnect-figma'; label: string }
	| { kind: 'connect-github'; label: string }
	| { kind: 'new-chat' }
	| { kind: 'publish' }
	| { kind: 'discard-changes' }
	| { kind: 'restore-version'; versionNumber?: number }
	| { kind: 'switch-to-latest' }
	| {
			kind: 'export-frames-code';
			label: string;
			framework: string;
			frameworkInstructions?: string;
			frameIds: string[];
	  }
	| {
			kind: 'export-frames-ai';
			label: string;
			preset: ExportFramesAiPreset;
			customBrief?: string;
			frameIds: string[];
	  }
	| {
			kind: 'export-frames-assets';
			label: string;
			format: 'png' | 'svg' | 'html' | 'html+images';
			frameIds: string[];
	  }
	| {
			kind: 'export-ds-ai';
			label: string;
			preset: 'all' | 'components' | 'tokens' | 'custom';
			componentNames?: string[];
			tokenDomains?: string[];
			customPrompt?: string;
	  }
	| {
			kind: 'choice';
			options: Array<{ label: string }>;
			reason?: string;
	  }
	| {
			kind: 'plan';
			planId: string;
			steps: Array<{ label: string }>;
	  }
	| { kind: 'open-prototype'; label: string; frameId: string }
	| { kind: 'follow-up'; label: string; prompt: string }
	| { kind: 'create-ds'; label: string };

export type ChatMessageActionPart = {
	type: 'action';
	action: ChatAction;
};

// Strip stray <frame> tags outside <frames> blocks (LLM artifacts)
// Matches both self-closing (<frame ... />) and paired (<frame ...>...</frame>)
const stripStrayFrameTags = (text: string): string =>
	text.replace(/<frame\s[^>]*\/>/g, '').replace(/<frame\s[^>]*>[\s\S]*?<\/frame>/g, '');

export const splitMessageWithFrames = (message: string): ChatMessagePart[] => {
	const parts: ChatMessagePart[] = [];
	// Strip legacy <design_group>/<design> XML tags from old chat messages
	message = message.replace(/<\/?design_group[^>]*>/g, '');
	message = message.replace(/<\/?design(?:\s[^>]*)?>/g, '');
	message = message.replace(/<!\[CDATA\[[\s\S]*?\]\]>/g, '');
	const combinedRegex = /\n?(?:<frames>[\s\S]*?<\/frames>|<action[\s\S]*?<\/action>)\n?/g;
	let lastIndex = 0;
	let match;

	while ((match = combinedRegex.exec(message)) !== null) {
		// Add text before the block
		if (match.index > lastIndex) {
			const text = stripStrayFrameTags(message.slice(lastIndex, match.index)).trim();
			if (text) {
				parts.push({ type: 'text', content: text });
			}
		}

		// Add the frames or action block
		const raw = match[0];
		if (raw.includes('<frames')) {
			parts.push({ type: 'frames', frameIds: parseFramesXml(raw) });
		} else if (raw.includes('<action')) {
			const action = parseActionXml(raw);
			if (action) {
				parts.push({ type: 'action', action });
			}
		}

		lastIndex = match.index + match[0].length;
	}

	// Add any remaining text after the last block
	if (lastIndex < message.length) {
		const text = stripStrayFrameTags(message.slice(lastIndex)).trim();
		if (text) {
			parts.push({ type: 'text', content: text });
		}
	}

	return parts;
};

// Parse ds-update XML tags from message text.
// Paths prefixed with "-" are deletions (convention from diff/git).
// The `type` attribute is a file-group prefix (mirrors ds_files/ directories).
const parseDsUpdateXml = (xml: string): { section: DsSection; paths: string[] } | null => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(xml, 'text/xml');
	const updateElement = doc.querySelector('*|ds-update');
	if (!updateElement) return null;
	const type = updateElement.getAttribute('type');
	if (type && (VALID_DS_SECTIONS as readonly string[]).includes(type)) {
		const pathsAttr =
			updateElement.getAttribute('paths') ?? updateElement.getAttribute('files') ?? '';
		const paths = pathsAttr
			? pathsAttr.split('|').filter((p) => p && !p.replace(/^-/, '').endsWith('.summary.md'))
			: [];
		if (paths.length === 0) return null;
		return { section: type as DsSection, paths };
	}
	return null;
};

// Parse <action> XML tags into a typed ChatAction discriminated union.
// returns a ChatAction or null if the kind is unrecognized or required fields are missing.
const VALID_ACTION_KINDS = [
	'try-in-project',
	'download-ds',
	'show',
	'generate-gallery',
	'connect-figma',
	'reconnect-figma',
	'connect-github',
	'new-chat',
	'publish',
	'discard-changes',
	'restore-version',
	'switch-to-latest',
	'export-frames-code',
	'export-frames-ai',
	'export-frames-assets',
	'export-ds-ai',
	'choice',
	'plan',
	'open-prototype',
	'follow-up',
	'create-ds',
] as const;
const VALID_AI_EXPORT_PRESETS = [
	'spec',
	'style',
	'custom',
] as const satisfies readonly ExportFramesAiPreset[];
const VALID_DS_AI_PRESETS = ['all', 'components', 'tokens', 'custom'] as const;
const VALID_ASSETS_FORMATS = ['png', 'svg', 'html', 'html+images'] as const;
const VALID_DOWNLOAD_FORMATS = ['bundle', 'tokens-json'] as const;

const parseActionXml = (xml: string): ChatAction | null => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(xml, 'text/xml');
	const el = doc.querySelector('*|action');
	if (!el) return null;

	const kind = el.getAttribute('type');
	if (!kind || !(VALID_ACTION_KINDS as readonly string[]).includes(kind)) return null;

	const label = (el.textContent || '').trim() || undefined;

	switch (kind) {
		case 'try-in-project': {
			if (!label) return null;
			const prompt = el.getAttribute('prompt') || undefined;
			const sceneId = el.getAttribute('scene-id') || undefined;
			return { kind: 'try-in-project', label, prompt, sceneId };
		}
		// DEPRECATED: kept so old chat messages still parse. LLM no longer emits this.
		case 'download-ds': {
			const format = el.getAttribute('format');
			if (!format || !(VALID_DOWNLOAD_FORMATS as readonly string[]).includes(format)) return null;
			return { kind: 'download-ds', format: format as 'bundle' | 'tokens-json', label };
		}
		case 'show': {
			const path = el.getAttribute('path') || undefined;
			return { kind: 'show', path };
		}
		case 'generate-gallery': {
			if (!label) return null;
			const prompt = el.getAttribute('prompt');
			if (!prompt) return null;
			return { kind: 'generate-gallery', label, prompt };
		}
		case 'connect-figma': {
			return { kind: 'connect-figma', label: label || 'Connect your Figma account' };
		}
		case 'reconnect-figma': {
			return { kind: 'reconnect-figma', label: label || 'Reconnect Figma' };
		}
		case 'connect-github': {
			return { kind: 'connect-github', label: label || 'Connect your GitHub account' };
		}
		case 'new-chat':
			return { kind: 'new-chat' };
		case 'publish':
			return { kind: 'publish' };
		case 'discard-changes':
			return { kind: 'discard-changes' };
		case 'restore-version': {
			const vAttr = el.getAttribute('version');
			const versionNumber = vAttr ? parseInt(vAttr, 10) : undefined;
			return {
				kind: 'restore-version',
				versionNumber: versionNumber && !isNaN(versionNumber) ? versionNumber : undefined,
			};
		}
		case 'switch-to-latest':
			return { kind: 'switch-to-latest' };
		case 'export-frames-code': {
			const framework = (el.getAttribute('framework') || '').trim();
			if (!framework) return null;
			const frameworkInstructions =
				el.getAttribute('frameworkInstructions') ||
				el.getAttribute('framework-instructions') ||
				undefined;
			const frameIdsAttr = el.getAttribute('frameIds') || el.getAttribute('frame-ids') || '';
			const frameIds = frameIdsAttr
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean);
			if (frameIds.length === 0) return null;
			return {
				kind: 'export-frames-code',
				label: label || 'Export code',
				framework,
				frameworkInstructions: frameworkInstructions?.trim() || undefined,
				frameIds,
			};
		}
		case 'export-frames-ai': {
			const preset = (el.getAttribute('preset') || '').trim();
			if (!(VALID_AI_EXPORT_PRESETS as readonly string[]).includes(preset)) return null;
			const customBrief = el.getAttribute('customBrief') || el.getAttribute('custom-brief') || '';
			const frameIdsAttr = el.getAttribute('frameIds') || el.getAttribute('frame-ids') || '';
			const frameIds = frameIdsAttr
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean);
			if (frameIds.length === 0) return null;
			if (preset === 'custom' && !customBrief.trim()) return null;
			return {
				kind: 'export-frames-ai',
				label: label || 'Export document',
				preset: preset as ExportFramesAiPreset,
				customBrief: customBrief.trim() || undefined,
				frameIds,
			};
		}
		case 'export-frames-assets': {
			const format = (el.getAttribute('format') || '').trim();
			if (!(VALID_ASSETS_FORMATS as readonly string[]).includes(format)) return null;
			const frameIdsAttr = el.getAttribute('frameIds') || el.getAttribute('frame-ids') || '';
			const frameIds = frameIdsAttr
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean);
			if (frameIds.length === 0) return null;
			return {
				kind: 'export-frames-assets',
				label: label || 'Export assets',
				format: format as 'png' | 'svg' | 'html' | 'html+images',
				frameIds,
			};
		}
		case 'choice': {
			const optionEls = el.querySelectorAll('*|option');
			const options = Array.from(optionEls)
				.map((o) => (o.textContent || '').trim())
				.filter(Boolean)
				.map((label) => ({ label }));
			if (options.length === 0) return null;
			const reason = el.getAttribute('reason') || undefined;
			return { kind: 'choice', options, reason };
		}
		case 'plan': {
			const planId = (el.getAttribute('planId') || el.getAttribute('plan-id') || '').trim();
			if (!planId) return null;
			const stepEls = el.querySelectorAll('*|step');
			const steps = Array.from(stepEls)
				.map((s) => (s.textContent || '').trim())
				.filter(Boolean)
				.map((label) => ({ label }));
			if (steps.length === 0) return null;
			return { kind: 'plan', planId, steps };
		}
		case 'export-ds-ai': {
			const preset = (el.getAttribute('preset') || '').trim();
			if (!(VALID_DS_AI_PRESETS as readonly string[]).includes(preset)) return null;
			const namesAttr =
				el.getAttribute('componentNames') || el.getAttribute('component-names') || '';
			const componentNames = namesAttr
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean);
			const domainsAttr = el.getAttribute('tokenDomains') || el.getAttribute('token-domains') || '';
			const tokenDomains = domainsAttr
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean);
			const customPrompt = (
				el.getAttribute('customPrompt') ||
				el.getAttribute('custom-prompt') ||
				''
			).trim();
			if (preset === 'components' && componentNames.length === 0) return null;
			if (preset === 'tokens' && tokenDomains.length === 0) return null;
			if (preset === 'custom' && !customPrompt) return null;
			return {
				kind: 'export-ds-ai',
				label: label || 'Export design system document',
				preset: preset as 'all' | 'components' | 'tokens' | 'custom',
				componentNames: componentNames.length ? componentNames : undefined,
				tokenDomains: tokenDomains.length ? tokenDomains : undefined,
				customPrompt: customPrompt || undefined,
			};
		}
		case 'open-prototype': {
			const frameId = (el.getAttribute('frameId') || el.getAttribute('frame-id') || '').trim();
			if (!frameId) return null;
			return { kind: 'open-prototype', label: label || 'Preview', frameId };
		}
		case 'follow-up': {
			const prompt = (el.getAttribute('prompt') || '').trim();
			if (!label || !prompt) return null;
			return { kind: 'follow-up', label, prompt };
		}
		case 'create-ds': {
			return { kind: 'create-ds', label: label || 'Create a design system' };
		}
		default:
			return null;
	}
};

// <plan-approved planId="..."/> is a zero-pixel state marker the plan orchestrator
// appends when a plan is approved. It's not an <action> (it doesn't render) — PlanAction
// reads the set of approved planIds via getApprovedPlanIds to decide whether to show
// its Approve button. Strip from rendered text so the tag itself never shows up.
const PLAN_APPROVED_RE = /\n?<plan-approved[^>]*\/>\n?/g;
const PLAN_APPROVED_ID_RE = /<plan-approved[^>]*\bplanId="([^"]+)"[^>]*\/>/g;

// <step-heading number label/> is the heading the plan orchestrator emits
// before each step's agent run. Parsed and rendered by StepHeading; stripped
// here so the raw tag never leaks into markdown.
const STEP_HEADING_RE = /\n?<step-heading[^>]*\/>\n?/g;

export const getApprovedPlanIds = (entries: ChatEntry[]): Set<string> => {
	const ids = new Set<string>();
	for (const entry of entries) {
		if (entry.kind !== 'system') continue;
		let match;
		PLAN_APPROVED_ID_RE.lastIndex = 0;
		while ((match = PLAN_APPROVED_ID_RE.exec(entry.message)) !== null) {
			ids.add(match[1]);
		}
	}
	return ids;
};

// Splits DS chat messages on both <ds-update> and <action> tags in a single pass.
export const splitMessageWithDsUpdates = (message: string): ChatMessagePart[] => {
	// Strip invisible <plan-approved/> markers and <step-heading/> tags so
	// they never leak into rendered markdown. Both have dedicated renderers.
	message = message.replace(PLAN_APPROVED_RE, '');
	message = message.replace(STEP_HEADING_RE, '');
	const parts: ChatMessagePart[] = [];
	const combinedRegex = /\n?(?:<ds-update[\s\S]*?<\/ds-update>|<action[\s\S]*?<\/action>)\n?/g;
	let lastIndex = 0;
	let match;

	while ((match = combinedRegex.exec(message)) !== null) {
		if (match.index > lastIndex) {
			const text = message.slice(lastIndex, match.index).trim();
			if (text) {
				parts.push({ type: 'text', content: text });
			}
		}

		const raw = match[0];
		if (raw.includes('<ds-update')) {
			const parsed = parseDsUpdateXml(raw);
			if (parsed) {
				parts.push({
					type: 'ds-update',
					section: parsed.section,
					paths: parsed.paths,
				});
			}
		} else if (raw.includes('<action')) {
			const action = parseActionXml(raw);
			if (action) {
				parts.push({ type: 'action', action });
			}
		}

		lastIndex = match.index + match[0].length;
	}

	// add any remaining text after the last XML block
	if (lastIndex < message.length) {
		const text = message.slice(lastIndex).trim();
		if (text) {
			parts.push({ type: 'text', content: text });
		}
	}

	return parts;
};


// Returns true if the chat was created by migration (contains typed site entries)
export const isMigratedFrameChat = (entries: ChatEntry[]): boolean => {
	return entries.some((e) => e.kind === 'site');
};


export const getSelectionSvg = async (
	selectionInfo: SelectionInfo,
	siteId: string
): Promise<Blob> => {
	// Send message to the existing iframe to get the selection SVG
	const svg = await new Promise<{ svg: string; bounds: { width: number; height: number } }>(
		(resolve, reject) => {
			const messageHandler = (event: MessageEvent) => {
				if (!isForgeOrigin(event)) return;
				if (event.data?.type !== 'elementSvg') return;
				if (event.data?.siteId !== siteId) return;

				window.removeEventListener('message', messageHandler);

				if (event.data.error) {
					reject(new Error(event.data.error));
					return;
				}

				const svg = event.data.svg as string;
				if (!svg) {
					reject(new Error('No SVG data received'));
					return;
				}
				resolve({
					svg,
					bounds: event.data.bounds,
				});
			};

			window.addEventListener('message', messageHandler);

			// Send the selection info to the iframe
			const iframe = document.querySelector('iframe') as HTMLIFrameElement | null;
			if (!iframe?.contentWindow) {
				window.removeEventListener('message', messageHandler);
				reject(new Error('No iframe found'));
				return;
			}

			// Use typed helper instead of raw postMessage
			sendToDevigner(iframe, siteId, {
				type: 'getSelectionSvg',
				selectionInfo,
			});

			// Set a timeout in case the iframe never responds
			setTimeout(() => {
				window.removeEventListener('message', messageHandler);
				reject(new Error('Timeout waiting for SVG data'));
			}, 10000); // 10 second timeout
		}
	);

	const response = await forge.inlineSvg({
		data: svg.svg,
		width: svg.bounds.width,
		height: svg.bounds.height,
	});
	if (!response.data) throw new Error('Failed to convert SVG to inline');

	return new Blob([response.data], { type: 'image/svg+xml' });
};
